import axios from "axios";
import { AvailableIntegration, Integration } from "@bageltechnologies/bagel-common";

const API_DOMAIN = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN;


const getAuthHeaders = async (auth0: any) => {
  try {
    const token = await auth0.getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  } catch (e) {
    throw e;
  }
};

export const getAvailableIntegrations = async (auth0: any): Promise<AvailableIntegration[]> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_DOMAIN}/integrations/v2/available`, config);
    return response.data;
  } catch (e) {
    console.error("Error fetching available integrations:", e);
    throw e;
  }
};

export const getIntegrations = async (auth0: any): Promise<Integration[]> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_DOMAIN}/integrations/v2/connected`, config);
    return response.data;
  } catch (e) {
    console.error("Error fetching connected integrations:", e);
    throw e;
  }
};

export const getIntegrationByKey = async (
  key: string,
  auth0: any
): Promise<{ available: AvailableIntegration; connected?: Integration }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_DOMAIN}/integrations/v2/${key}`, config);
    return response.data;
  } catch (e) {
    console.error(`Error fetching integration ${key}:`, e);
    throw e;
  }
};

export const updateIntegrationConfig = async (
  key: string,
  config: Partial<Integration>,
  auth0: any
): Promise<Integration> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.put(
      `${API_DOMAIN}/integrations/v2/${key}/config`,
      config,
      authConfig
    );
    return response.data;
  } catch (e) {
    console.error(`Error updating integration ${key} config:`, e);
    throw e;
  }
};

export interface FieldsResponse {
  fields: Record<string, any>[];
  origin: string;
  blacklistedFields?: string[];
}

export interface IntegrationTask {
  _id: string;
  type: string;
  objectName: string;
  accountId: string;
  origin: string;
  options: {
    objectName: string;
    startDate: string;
    endDate: string;
    dateField: string;
  };
  execution: {
    startDate: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    finishDate?: string;
    stats?: {
      apiCallCount?: number;
      receivedCount?: number;
      processedCount?: number;
    };
  };
  lastModified: string;
}

export interface TasksResponse {
  tasks: IntegrationTask[];
  pagination: {
    skip: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export const getIntegrationFields = async (
  key: string,
  object: string,
  originObject: string,
  auth0: any
): Promise<FieldsResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    console.log('getIntegrationFields', key, object, originObject);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/${key}/object2.0/${object}/originObject/${originObject}`,
      authConfig
    );
    return response.data;
  } catch (e) {
    // If fields are not found, return empty fields object with the integration key as origin
    if (axios.isAxiosError(e) && e.response?.status === 404) {
      return {
        fields: [],
        origin: key
      };
    }
    console.error(`Error getting integration ${key} fields for ${object}:`, e);
    throw e;
  }
};

export type UpdateIntegrationFieldsRequest =
  | {
      fieldName: string;
      field: {
        fieldType: 'imported' | 'processed';
        [key: string]: any;
      };
    }
  | {
      blacklistField: {
        action: 'add' | 'remove';
        field: string;
      };
    };

export type UpdateIntegrationFieldsResponse =
  | {
      success: boolean;
      modifiedCount: number;
      upsertedId?: string;
    }
  | {
      success: boolean;
      blacklistedFields: string[];
      modifiedCount: number;
      upsertedId?: string;
    };

export const updateIntegrationFields = async (
  key: string,
  object: string,
  originObject: string,
  fieldData: UpdateIntegrationFieldsRequest,
  auth0: any
): Promise<UpdateIntegrationFieldsResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    console.log('updateIntegrationFields', key, object, originObject, fieldData);
    const response = await axios.put(
      `${API_DOMAIN}/integrations/v2/${key}/object2.0/${object}/originObject/${originObject}`,
      fieldData,
      authConfig    
    );
    return response.data;
  } catch (e) {
    console.error(`Error updating integration ${key} fields for ${object}:`, e);
    throw e;
  }
};

export const updateIntegrationApiKey = async (
  key: string,
  auth0: any,
  tokenObject?: any,
): Promise<Integration> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.put(
      `${API_DOMAIN}/integrations/v2/${key}/apiKey`,
      tokenObject,
      authConfig
    );
    return response.data;
  } catch (e) {
    console.error(`Error updating integration ${key} config:`, e);
    throw e;
  }
};

export const connectIntegration = async (
  key: string,
  code: string,
  auth0: any
): Promise<Integration> => {
  try {
    const config = await getAuthHeaders(auth0);
    console.log({key, code})
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/connect`,
      { code },
      config
    );
    return response.data;
  } catch (e) {
    console.error(`Error connecting integration ${key}:`, e);
    throw e;
  }
};

export const disconnectIntegration = async (
  key: string,
  auth0: any
): Promise<void> => {
  try {
    const config = await getAuthHeaders(auth0);
    await axios.post(`${API_DOMAIN}/integrations/v2/${key}/disconnect`, {}, config);
  } catch (e) {
    console.error(`Error disconnecting integration ${key}:`, e);
    throw e;
  }
};

export const getOAuthUrl = async (
  key: string,
  auth0: any
): Promise<{ url: string }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/${key}/oauth-url`,
      config
    );
    return response.data;
  } catch (e) {
    console.error(`Error getting OAuth URL for ${key}:`, e);
    throw e;
  }
}; 

export const getConnectionOptions = async (
  key: string,
  auth0: any
): Promise<{ url: string }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/${key}/connection-options`,
      config
    );
    return response.data;
  } catch (e) {
    console.error(`Error getting OAuth URL for ${key}:`, e);
    throw e;
  }
}; 


/**
 * Trigger a sync run for one or more objects of an integration.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param objects Array of object names to sync (e.g., ['accounts', 'opportunities'])
 * @param dateRange Optional date range configuration
 * @returns The triggered sync run results
 */
export const triggerIntegrationSyncRun = async (
  auth0: any,
  key: string,
  objects: string[],
  dateRange?: {
    start: string;
    end: string;
    dateField?: string;
  }
): Promise<any> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN}/integrations/v2/${key}/runs`;
    const requestBody: any = { objects };
    
    if (dateRange) {
      requestBody.dateRange = dateRange;
    }
    
    const response = await axios.post(
      url,
      requestBody,
      config
    );
    return response.data;
  } catch (e) {
    console.error("Error triggering integration sync run", e);
    throw e;
  }
};

/**
 * Fetch a paginated list of tasks for an integration.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param skip How many tasks to skip (for pagination, default 0)
 * @param limit How many tasks to return (default 20)
 * @param status Optional status parameter for filtering tasks (can be comma-separated string)
 * @returns TasksResponse with tasks array and pagination info
 */
export const getIntegrationTasks = async (
  auth0: any,
  key: string,
  skip = 0,
  limit = 20,
  status?: string
): Promise<TasksResponse> => {
  try {
    const config = await getAuthHeaders(auth0);
    let url = `${window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN}/integrations/v2/${key}/tasks?skip=${skip}&limit=${limit}`;
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    console.log("getIntegrationTasks", url);

    const response = await axios.get(url, config);
    return response.data;
  } catch (e) {
    console.error("Error fetching integration tasks", e);
    throw e;
  }
};

/**
 * Fetch paginated tasks for a specific integration object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'salesforce')
 * @param object Object name (e.g., 'accounts')
 * @param skip How many tasks to skip (for pagination, default 0)
 * @param limit How many tasks to return (default 20)
 * @param status Optional status parameter for filtering tasks (can be comma-separated string)
 * @returns TasksResponse with tasks array and pagination info for the specified object
 */
export const fetchIntegrationObjectTasks = async (
  auth0: any,
  key: string,
  object: string,
  skip = 0,
  limit = 20,
  status?: string
): Promise<TasksResponse> => {
  try {
    const config = await getAuthHeaders(auth0);
    let url = `${API_DOMAIN}/integrations/v2/${key}/tasks/${object}?skip=${skip}&limit=${limit}`;
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    console.log("fetchIntegrationObjectTasks", url);

    const response = await axios.get(url, config);
    return response.data;
  } catch (e) {
    console.error(`Error fetching tasks for integration ${key}, object ${object}:`, e);
    throw e;
  }
};

/**
 * Triggers a metadata sync for a specific object of an integration and returns the updated metadata and sync result.
 * @param key Integration key (e.g., 'salesforce', 'jira')
 * @param object Object name to update metadata for (e.g., 'Opportunity', 'Ticket')
 * @param auth0 Auth object for authentication
 * @returns The updated object metadata and sync result
 */
export const updateIntegrationObjectMeta = async (
  key: string,
  object: string,
  auth0: any
): Promise<any> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/runs/${object}/update-meta`;
    const response = await axios.put(url, undefined, config);
    return response.data;
  } catch (e) {
    console.error(`Error updating metadata for integration ${key}, object ${object}:`, e);
    throw e;
  }
};

/**
 * Get the count of processed and unprocessed items for a given integration and object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'airtable')
 * @param object Object name (e.g., 'survey')
 * @returns {Promise<{ processed: number, unprocessed: number }>} The counts of processed and unprocessed items
 */
export const getUnprocessedItemCount = async (
  auth0: any,
  key: string,
  originObjectKey: string
): Promise<{ processed: number; unprocessed: number }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/process/${originObjectKey}/count`;
    const response = await axios.get(url, config);
    return response.data;
  } catch (e) {
    console.error(`Error fetching processed and unprocessed item counts for integration ${key}, object ${originObjectKey}:`, e);
    throw e;
  }
};

/**
 * Process all unprocessed items for a given integration and object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'airtable')
 * @param originObjectKey Origin object key (e.g., 'survey')
 * @returns {Promise<{ processed: number, failed: number, errors: any[] }>} Processing result
 */
export const processUnprocessedItems = async (
  auth0: any,
  key: string,
  originObjectKey: string
): Promise<{ processed: number; failed: number; errors: any[] }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/process/${originObjectKey}`;
    const response = await axios.post(url, undefined, config);
    return response.data;
  } catch (e) {
    console.error(`Error processing unprocessed items for integration ${key}, originObjectKey ${originObjectKey}:`, e);
    throw e;
  }
};

/**
 * Get the count of missing organizations for a given integration and object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'salesforce')
 * @param object Object2.0 name (e.g., 'opportunity')
 * @param originObjectKey Origin object key from external system (e.g., 'Opportunity')
 * @returns {Promise<{ itemType: string, integrationKey: string, mappedField: string, missingOrgsCount: number }>} Missing organizations count info
 */
export const getMissingOrganizationsCount = async (
  auth0: any,
  key: string,
  object: string,
  originObjectKey: string
): Promise<{ itemType: string; integrationKey: string; mappedField: string; missingOrgsCount: number }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/missing-orgs/${object}/originObject/${originObjectKey}/count`;
    const response = await axios.get(url, config);
    return response.data;
  } catch (e) {
    console.error(`Error fetching missing organizations count for integration ${key}, object ${object}, originObject ${originObjectKey}:`, e);
    throw e;
  }
};

/**
 * Fill missing organizations for a given integration and object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'salesforce')
 * @param object Object2.0 name (e.g., 'opportunity')
 * @param originObjectKey Origin object key from external system (e.g., 'Opportunity')
 * @returns {Promise<{ itemType: string, integrationKey: string, mappedField: string, totalMissingOrgs: number, batchesProcessed: number, totalInserted: number }>} Fill organizations result
 */
export const fillMissingOrganizations = async (
  auth0: any,
  key: string,
  object: string,
  originObjectKey: string
): Promise<{ itemType: string; integrationKey: string; mappedField: string; totalMissingOrgs: number; batchesProcessed: number; totalInserted: number }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/missing-orgs/${object}/originObject/${originObjectKey}/fill`;
    const response = await axios.post(url, undefined, config);
    return response.data;
  } catch (e) {
    console.error(`Error filling missing organizations for integration ${key}, object ${object}, originObject ${originObjectKey}:`, e);
    throw e;
  }
};

export interface ExploreFieldsResponse {
  integrationKey: string;
  objectName: string;
  originObjectKey: string;
  totalFields: number;
  fieldsWithStatistics: number;
  totalDataRecords: number;
  lastUpdated: string;
  fields: Array<{
    // Original field metadata
    name: string;
    key: string;
    label: string;
    type: string;
    required?: boolean;
    length?: number;
    
    // Complete statistics data
    fillPercentage?: number;
    hasStatistics?: boolean;
    statisticsId?: string;
    totalRecords?: number;
    nonNullCount?: number;
    lastCalculated?: string;
    types?: string[];
    
    // Full values array
    values?: string[];
    
    // For numeric fields
    min?: number | string;
    max?: number | string;
    
    // Common values with frequency
    commonValues?: Array<{ value: string; count: number; percentage: number }>;
    uniqueValueCount?: number;
  }>;
}

/**
 * Get complete field metadata with statistics and values for a specific integration object.
 * @param key Integration key (e.g., 'salesforce', 'jira')
 * @param object Object name (e.g., 'account', 'opportunity')
 * @param originObjectKey Origin object key from external system (e.g., 'Account', 'Opportunity')
 * @param auth0 Auth object for authentication
 * @returns Complete field exploration data with statistics and values
 */
export const getIntegrationExploreFields = async (
  key: string,
  object: string,
  originObjectKey: string,
  auth0: any
): Promise<ExploreFieldsResponse> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/${object}/originObject/${originObjectKey}/explore-fields`;
    const response = await axios.get(url, config);
    return response.data;
  } catch (e) {
    console.error(`Error fetching explore fields for integration ${key}, object ${object}, originObject ${originObjectKey}:`, e);
    throw e;
  }
};

export interface PrepareMetaResponse {
  availableObjects: Array<{
    name: string;
    bagelObjectKey: string;
    fields: Array<{
      name: string;
      type: string;
      fieldPath: string;
    }>;
  }>;
  result: {
    tasksCompleted: number;
    itemsProcessed: number;
    errors: any[];
    metadataSynced: boolean;
  };
}

/**
 * Syncs and prepares integration metadata by fetching available objects from the integration platform.
 * @param key Integration key (e.g., 'notion', 'salesforce')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<PrepareMetaResponse> - Available objects and sync result
 */
export const prepareIntegrationMeta = async (
  key: string,
  auth0: any
): Promise<PrepareMetaResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/prepare-meta`,
      {},
      authConfig
    );
    return response.data;
  } catch (e) {
    console.error(`Error preparing integration ${key} metadata:`, e);
    throw e;
  }
};

export interface TestCredentialsResponse {
  valid: boolean;
  message: string;
  integrationKey: string;
  tested: {
    date: string;
    result: boolean;
    error?: string;
  };
  error?: string;
}

/**
 * Tests if integration credentials are valid and saves the test result to the integration.
 * @param key Integration key (e.g., 'salesforce', 'notion')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<TestCredentialsResponse> - Validation result and test metadata
 */
export const testIntegrationCredentials = async (
  key: string,
  auth0: any
): Promise<TestCredentialsResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/test-credentials`,
      {},
      authConfig
    );
    return response.data;
  } catch (e) {
    console.error(`Error testing integration ${key} credentials:`, e);
    throw e;
  }
};

export interface MLProcessedCountResponse {
  integrationKey: string;
  originObjectKey: string;
  totalItemsCount: number;
  totalMLEligible: number;
  mlProcessedCount: number;
  mlPendingCount: number;
  mlFilters: any;
  timestamp: string;
}

/**
 * Get ML processed count for a specific integration object.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param originObjectKey Object name (e.g., 'Case', 'Ticket')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<MLProcessedCountResponse> - ML processing count information
 */
export const getMLProcessedCount = async (
  key: string,
  originObjectKey: string,
  auth0: any
): Promise<MLProcessedCountResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/${key}/ml/${originObjectKey}/processed-count`,
      authConfig
    );
    return response.data;
  } catch (e) {
    console.error(`Error getting ML processed count for integration ${key}, object ${originObjectKey}:`, e);
    throw e;
  }
};

export interface MLItemUpdateRequest {
  updateType: string;
  processAll: boolean;
  additionalData?: any;
}

export interface MLItemUpdateResponse {
  success: boolean;
  integrationKey: string;
  originObjectKey: string;
  processedCount: number;
  processAll: boolean;
  processedItems: Array<{
    cxItemId: string;
    originId: string;
    itemType: string;
    wasAlreadyProcessed: boolean;
  }>;
  updateType: string;
  timestamp: string;
  message: string;
}

/**
 * Send ML item update for a specific integration object.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param originObjectKey Object name (e.g., 'Case', 'Ticket')
 * @param updateData ML update data
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<MLItemUpdateResponse> - ML update result
 */
export const sendMLItemUpdate = async (
  key: string,
  originObjectKey: string,
  updateData: MLItemUpdateRequest,
  auth0: any
): Promise<MLItemUpdateResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/ml/${originObjectKey}/item-update`,
      updateData,
      authConfig
    );
    return response.data;
  } catch (e) {
    console.error(`Error sending ML item update for integration ${key}, object ${originObjectKey}:`, e);
    throw e;
  }
};

export interface UpdateExternalObjectRequest {
  [key: string]: any;
}

export interface UpdateExternalObjectResponse {
  success: boolean;
  modifiedCount: number;
  matchedCount: number;
}

/**
 * Update an external object configuration for a specific integration.
 * @param key Integration key (e.g., 'airtable', 'salesforce')
 * @param originObjectKey Origin object key (e.g., 'ideas', 'opportunities')
 * @param updates Object containing fields to update
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<UpdateExternalObjectResponse> - Update result with counts
 */
export const updateExternalObject = async (
  key: string,
  originObjectKey: string,
  updates: UpdateExternalObjectRequest,
  auth0: any
): Promise<UpdateExternalObjectResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.patch(
      `${API_DOMAIN}/integrations/v2/${key}/external-object/${originObjectKey}`,
      updates,
      authConfig
    );
    return response.data;
  } catch (e) {
    console.error(`Error updating external object for integration ${key}, object ${originObjectKey}:`, e);
    throw e;
  }
};